import { useEffect, useRef, useState, type RefObject } from 'react';
import { useEscapeKey } from './useEscapeKey';

/**
 * M5.1 (issue #21) — extracted from `NewMenu.tsx`'s own open/outside-click/
 * Escape logic (M4.2, issue #16), unchanged in behavior, so the Export and
 * Copy menus this milestone adds (and Theme/Layout/Share in M6) don't each
 * carry their own copy of the same wiring. M6.1 (issue #28) pulls the
 * Escape-specific half out further into `useEscapeKey`, shared with
 * `ShortcutCheatsheet`'s own Escape-to-close.
 */
export interface Menu<T extends HTMLElement> {
  isOpen: boolean;
  /** Attach to the menu's outer container — a pointerdown outside it closes the menu. */
  containerRef: RefObject<T>;
  toggle: () => void;
  close: () => void;
}

export function useMenu<T extends HTMLElement = HTMLDivElement>(): Menu<T> {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<T>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  useEscapeKey(() => setIsOpen(false), isOpen);

  return {
    isOpen,
    containerRef,
    toggle: () => setIsOpen((open) => !open),
    close: () => setIsOpen(false),
  };
}
